import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { HslColorPicker } from 'react-colorful';
import { cn } from '../../utils/cn';
import { hexToHsl, hslFromPicker, hslToHex, normalizeHex } from './colorWheel';

interface ColorPickerPopoverProps {
  color: string;
  onChange: (color: string) => void;
  label: string;
  /** Theme Manager stays light. Import review uses dark Roary chrome. */
  variant?: 'light' | 'dark';
}

export const ColorPickerPopover: React.FC<ColorPickerPopoverProps> = ({
  color,
  onChange,
  label,
  variant = 'light',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localColor, setLocalColor] = useState(color);
  const isDark = variant === 'dark';

  useEffect(() => {
    setLocalColor(color);
  }, [color]);

  const handleColorChange = (newColor: string) => {
    setLocalColor(newColor);
    const hex = normalizeHex(newColor);
    if (hex) onChange(hex);
  };

  const handleWheelChange = (next: { h: number; s: number; l: number }) => {
    const lifted = hslFromPicker(hexToHsl(localColor), next);
    handleColorChange(hslToHex(lifted));
  };

  return (
    <div className="relative">
      <div
        className={cn(isDark ? 'flex flex-col gap-1' : 'flex items-center justify-between mb-2')}
      >
        <label
          className={cn(
            'text-sm font-medium',
            isDark ? 'text-xs text-slate-400 capitalize' : 'text-gray-700'
          )}
        >
          {label}
        </label>
        <button
          type="button"
          aria-label={`Pick ${label}`}
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-colors',
            isDark
              ? 'w-full bg-slate-900 border border-slate-700 hover:border-orange-500 text-white'
              : 'bg-white border border-gray-300 hover:border-gray-400'
          )}
        >
          <div
            className={cn(
              'w-5 h-5 rounded border',
              isDark ? 'border-slate-600' : 'border-gray-300'
            )}
            style={{ backgroundColor: localColor }}
          />
          <span className={cn('text-sm font-mono', isDark ? 'text-slate-200' : 'text-gray-600')}>
            {localColor}
          </span>
          <ChevronDown
            className={cn('w-4 h-4 transition-transform ml-auto', isOpen && 'rotate-180')}
          />
        </button>
      </div>

      {isOpen && (
        <>
          {/* Backdrop to close color picker when clicking outside */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            className={cn(
              'absolute right-0 z-50 mt-2 p-4 rounded-lg shadow-xl border',
              isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
            )}
          >
            <HslColorPicker color={hexToHsl(localColor)} onChange={handleWheelChange} />
            <input
              type="text"
              aria-label={`${label} hex`}
              value={localColor}
              onChange={e => handleColorChange(e.target.value)}
              className={cn(
                'mt-3 w-full px-3 py-1.5 text-sm font-mono rounded focus:outline-none focus:ring-2 focus:ring-primary-500',
                isDark
                  ? 'bg-slate-900 border border-slate-600 text-white'
                  : 'border border-gray-300'
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};
